/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { Component, ComponentProps, createRef, ElementRef } from 'react';
import { StyleSheet } from 'react-native';
import { Link, Text, TextInput } from './uiold';
import { themeColors } from './uiold/Constants';

import LoadingIndicator from './uiold/LoadingIndicator';

import loadGoogleMapsAPI, {
  GoogleMap,
  Place as GooglePlace,
} from '../support/loadGoogleMapsAPI';

export type Place = {
  id: string;
  textName: string;
  latitude: number;
  longitude: number;
};

type TextInputProps = ComponentProps<typeof TextInput>;

type ParentProps = Omit<TextInputProps, 'ref'> & {
  placeholder?: string | null | undefined;
  onPlaceSelect: (place: Place) => void;
};

type ParentState = {
  googleMap: GoogleMap | null;
  fetchState: 'loading' | 'success' | 'failure';
};

type AutocompleteProps = ParentProps & {
  googleMap: GoogleMap | null;
};

type TextInputInstance = ElementRef<typeof TextInput>;

export default class GooglePlacesAutocompleteParent extends Component<
  ParentProps,
  ParentState
> {
  state: ParentState = {
    googleMap: null,
    fetchState: 'loading',
  };

  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
    this._loadGoogleMapsAPI();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  render() {
    let { props } = this;
    let { googleMap, fetchState } = this.state;

    switch (fetchState) {
      case 'loading': {
        return <LoadingIndicator />;
      }
      case 'success': {
        return <GooglePlacesAutocomplete googleMap={googleMap} {...props} />;
      }
      case 'failure': {
        return (
          <Text style={styles.error}>
            {t.jsx(
              'Failed to load location information. <link>Click here</link> to try again.',
              {
                link: (content) => (
                  <Link
                    style={styles.linkText}
                    onPress={() => this._loadGoogleMapsAPI()}
                  >
                    {content}
                  </Link>
                ),
              },
            )}
          </Text>
        );
      }
      default:
        return null;
    }
  }

  _loadGoogleMapsAPI = async () => {
    this.setState({ fetchState: 'loading' });
    try {
      let googleMap = await loadGoogleMapsAPI();
      if (this._isMounted) {
        this.setState({ fetchState: 'success', googleMap });
      }
    } catch (e) {
      if (this._isMounted) {
        this.setState({ fetchState: 'failure' });
      }
    }
  };
}

class GooglePlacesAutocomplete extends Component<AutocompleteProps> {
  _ref = createRef<TextInputInstance>();

  componentDidMount() {
    let inputEl = this._ref.current?.getNativeHandle();
    let { googleMap } = this.props;
    if (inputEl != null && googleMap != null) {
      let autocomplete = new googleMap.places.Autocomplete(
        inputEl as HTMLInputElement,
      );
      autocomplete.addListener('place_changed', () => {
        let place = autocomplete.getPlace();
        if (place.geometry) {
          this._onPlaceSelect(place);
        }
      });
    }
  }

  render() {
    let { placeholder, googleMap, onPlaceSelect, ...otherProps } = this.props;
    if (placeholder == null) {
      // Prevent Google default "Enter a location" placeholder.
      placeholder = '';
    }
    return (
      <TextInput {...otherProps} placeholder={placeholder} ref={this._ref} />
    );
  }

  _onPlaceSelect = (place: GooglePlace) => {
    let textName = place.formatted_address;
    let latitude = place.geometry.location.lat();
    let longitude = place.geometry.location.lng();
    this.props.onPlaceSelect({
      id: place.place_id,
      textName,
      latitude,
      longitude,
    });
  };
}

let styles = StyleSheet.create({
  error: {
    fontWeight: 'normal',
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: themeColors.errorBorderRed,
    backgroundColor: themeColors.errorBackgroundRed,
  },
  linkText: {
    textDecorationLine: 'underline',
  },
});
